

// ###########################################################

import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Grid
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import AuthService from "services/auth-service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import UserService from "services/user-service";
import { AuthContext } from "context";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW]

const Account = () => {
  const authContext = useContext(AuthContext);
  const [workspaceProfile, setWorkspaceProfile] = useState({ name: "", email: "", phone: "" });
  const [editMode, setEditMode] = useState(false);
  const [updatedWorkspaceProfile, setUpdatedWorkspaceProfile] = useState({ name: "", phone: "" });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDemoPopup, setShowDemoPopup] = useState(false); // [NEW] Demo Popup State
  const [demoFeature, setDemoFeature] = useState(""); // [NEW] Feature name for popup

  useEffect(() => {
    const fetchWorkspaceProfile = async () => {
      try {
        const response = await AuthService.getProfile();
        setWorkspaceProfile(response.data.attributes);
        setUpdatedWorkspaceProfile(response.data.attributes);
      } catch (error) {
        console.error("Error fetching workspace profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkspaceProfile();
  }, []);

  const handleEdit = () => {
    // [NEW] Live Demo Check
    const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
    if (isDemo) {
      setDemoFeature("Updating Profile");
      setShowDemoPopup(true);
      return;
    }
    setEditMode(true);
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        data: {
          type: "user-update",
          attributes: {
            name: updatedWorkspaceProfile.name,
            phone: updatedWorkspaceProfile.phone,
          },
        },
      };

      await AuthService.updateProfile(payload);

      setWorkspaceProfile(updatedWorkspaceProfile);
      setEditMode(false);

      // 🔥 Notify Header to refresh user data
      window.dispatchEvent(new Event("profile-updated"));

    } catch (error) {
      console.error("Error updating workspace profile:", error);
    }
  };

  const handleCancel = () => {
    setUpdatedWorkspaceProfile(workspaceProfile); // Restore original values
    setEditMode(false);
  };

  const handleDeleteAccount = () => {
    // [NEW] Live Demo Check
    const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
    if (isDemo) {
      setDemoFeature("Deleting Account");
      setShowDemoPopup(true);
      return;
    }
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await UserService.deleteUserAccount();
      const response = await AuthService.logout();
      authContext.logout();
    } catch (error) {
      console.error("❌ Error deleting account:", error);
      const msg = error.message || error.response?.data?.message || "Failed to delete account. Please try again.";
      setErrorMessage(msg);
      setOpenErrorDialog(true);
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  return (
    <Card sx={{ mb: 3, mt: 7, overflow: "visible" }}>
      <MDBox
        mx={2}
        mt={-3}
        py={2}
        px={2}
        variant="gradient"
        bgColor="info"
        borderRadius="lg"
        coloredShadow="info"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <MDTypography variant="h6" color="white">
          Account Information
        </MDTypography>
      </MDBox>

      <MDBox p={2}>
        <Box sx={{ backgroundColor: "#f8f9fa", p: 2, borderRadius: 2 }}>
          {loading ? (
            <MDTypography variant="body2" color="text" opacity={0.6}>
              Loading account details...
            </MDTypography>
          ) : (
            <Grid container spacing={2}>

              {/* Full Name */}
              <Grid item xs={4}>
                <MDTypography variant="subtitle2" color="text" fontWeight="medium">Full Name</MDTypography>
              </Grid>
              <Grid item xs={8}>
                {editMode ? (
                  <TextField
                    fullWidth
                    value={updatedWorkspaceProfile.name}
                    onChange={(e) => setUpdatedWorkspaceProfile({ ...updatedWorkspaceProfile, name: e.target.value })}
                  />
                ) : (
                  <Typography variant="body2" fontWeight="bold">
                    {workspaceProfile.name || "N/A"}
                  </Typography>
                )}
              </Grid>

              {/* Email Address */}
              <Grid item xs={4}>
                <MDTypography variant="subtitle2" color="text" fontWeight="medium">Email Address</MDTypography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body2" fontWeight="bold">{workspaceProfile.email || "N/A"}</Typography>
              </Grid>
            </Grid>
          )}

          {/* Buttons */}
          <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
            {editMode ? (
              <>
                <Button startIcon={<CancelIcon />} color="secondary" onClick={handleCancel}>
                  CANCEL
                </Button>
                <Button startIcon={<SaveIcon />} color="primary" onClick={handleUpdate}>
                  UPDATE
                </Button>
              </>
            ) : (
              <Button startIcon={<EditIcon />} color="primary" onClick={handleEdit}>
                EDIT
              </Button>
            )}
            <Button startIcon={<DeleteIcon />} color="error" onClick={handleDeleteAccount}>
              DELETE
            </Button>
          </Box>
        </Box>
      </MDBox>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Account Deletion</DialogTitle>
        <DialogContent>
          <MDTypography variant="subtitle2">
            Are you sure you want to delete your account? This action cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)}>
        <DialogTitle>Action Required</DialogTitle>
        <DialogContent>
          <MDTypography variant="subtitle2">
            {errorMessage}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenErrorDialog(false)} color="primary">OK</Button>
        </DialogActions>
      </Dialog>
      {/* [NEW] Live Demo Restriction Popup */}
      <LiveDemoRestriction
        open={showDemoPopup}
        onClose={() => setShowDemoPopup(false)}
        featureName={demoFeature}
      />
    </Card >
  );
};

export default Account;
